import React from 'react';
import { Database, ArrowRightLeft, RefreshCw, Shield, Clock, Bot } from 'lucide-react';
import { SEO } from '../../components/SEO';

export function ErpMigration() {
  return (
    <div className="min-h-screen">
      <SEO
        title="ERP Data Migration | Doyen AI"
        description="Seamlessly migrate your ERP data with AI-assisted automation"
        url={`${window.location.origin}/products/erp-migration`}
      />

      {/* Header Section with Animated Stats */}
      <section className="relative bg-card pt-32 pb-24">
        {/* Grid Pattern Background */}
        <div className="absolute inset-0 grid-pattern opacity-30" />
        
        {/* Content */}
        <div className="relative">
          <div className="text-center mb-16">
            <h1 className="text-5xl font-bold text-primary mb-6">Reduce migration time using AI automation</h1>
            <p className="text-xl text-neutral-600 max-w-3xl mx-auto px-4">
              Maintain highest quality financial data while migrating chart of accounts, trial balances, journals, and multi-entity support
            </p>
          </div>

          {/* Animated Stats */}
          <div className="max-w-5xl mx-auto px-4 grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="animate-fade-in">
              <div className="bg-white rounded-xl p-8 shadow-sm border border-neutral-200">
                <div className="text-4xl font-bold text-secondary mb-3">90%</div>
                <div className="text-xl font-semibold text-primary mb-2">Faster Migration</div>
                <p className="text-neutral-600">Reduce migration time from months to days with AI automation</p>
              </div>
            </div>

            <div className="animate-fade-in animation-delay-500">
              <div className="bg-white rounded-xl p-8 shadow-sm border border-neutral-200">
                <div className="text-4xl font-bold text-secondary mb-3">100%</div>
                <div className="text-xl font-semibold text-primary mb-2">Data Accuracy</div>
                <p className="text-neutral-600">Ensure complete data integrity with intelligent validation</p>
              </div>
            </div>

            <div className="animate-fade-in animation-delay-1000">
              <div className="bg-white rounded-xl p-8 shadow-sm border border-neutral-200">
                <div className="text-4xl font-bold text-secondary mb-3">24/7</div>
                <div className="text-xl font-semibold text-primary mb-2">AI Support</div>
                <p className="text-neutral-600">Get instant help from our AI co-pilot at any time</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Value Props Section */}
      <section className="bg-surface py-24">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-card rounded-xl p-8 shadow-sm border border-neutral-200 hover:shadow-md transition-shadow">
              <Bot className="w-12 h-12 text-secondary mb-6" />
              <h3 className="text-2xl font-bold text-primary mb-4">AI Agent and Co-Pilot Built for Finance</h3>
              <p className="text-neutral-600">
                Get expert guidance from an AI trained on financial data migrations. It assists with mapping, identifies errors, and ensures your team stays unblocked.
              </p>
            </div>
            
            <div className="bg-card rounded-xl p-8 shadow-sm border border-neutral-200 hover:shadow-md transition-shadow">
              <Shield className="w-12 h-12 text-secondary mb-6" />
              <h3 className="text-2xl font-bold text-primary mb-4">You're in Control</h3>
              <p className="text-neutral-600">
                Maintain full oversight and approval at every step. Your data stays clean, complete, and traceable—ready for audit from day one.
              </p>
            </div>

            <div className="bg-card rounded-xl p-8 shadow-sm border border-neutral-200 hover:shadow-md transition-shadow">
              <Clock className="w-12 h-12 text-secondary mb-6" />
              <h3 className="text-2xl font-bold text-primary mb-4">Accelerate Time to Value</h3>
              <p className="text-neutral-600">
                Eliminate manual spreadsheets and weeks of clean-up work with intelligent automation and validation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="bg-surface py-24">
        <div className="max-w-7xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-primary text-center mb-12">How It Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {[
              {
                title: "Data Extraction",
                description: "Automatically generate data schemas from existing source systems.",
                icon: Database
              },
              {
                title: "Data Transformation",
                description: "Map and transform extracted data into your target format.",
                icon: ArrowRightLeft
              },
              {
                title: "Incremental Migration",
                description: "Support for full migrations and incremental updates.",
                icon: RefreshCw
              },
              {
                title: "Reconciliation",
                description: "Ensure data integrity between source and destination systems.",
                icon: Shield
              }
            ].map((step, index) => (
              <div key={index} className="bg-card rounded-xl p-6 shadow-sm border border-neutral-200 hover:shadow-md transition-shadow">
                <step.icon className="w-12 h-12 text-secondary mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-center text-primary">{step.title}</h3>
                <p className="text-neutral-600 text-sm text-center">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-surface py-24">
        <div className="max-w-4xl mx-auto px-4">
          <div className="bg-gradient-to-br from-secondary to-secondary-dark text-white py-24 rounded-xl">
            <div className="max-w-3xl mx-auto px-4 text-center">
              <h2 className="text-3xl font-bold mb-6">Ready to Transform Your ERP Migration?</h2>
              <a 
                href="mailto:sales@doyen.com"
                className="inline-flex items-center px-6 py-3 border-2 border-white rounded-lg text-lg font-semibold hover:bg-white hover:text-secondary transition-colors"
              >
                Contact Us
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}